// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';

import { Separator, Button } from '../../../commons/atoms';

import {
  usePendingIncomingPayment,
  getMerchant,
  getCountry,
  getTokenizedCard,
  getRedirectUrlAfterPayment,
} from '@tectonics/prs/incoming-payment';
import { getName } from '@tectonics/prs/merchant';
import { type Country } from '@tectonics/prs/country';
import { Empty } from '@tectonics/components';
import { getLastFourDigits } from '@tectonics/prs/card/tokenized';
import { useCurrentReceipt } from '@tectonics/prs/receipts';
import { getMoneyFormat } from '@tectonics/look-and-feel/shopify/commons/money-format';
import { amountToCurrencyString } from '@tectonics/prs/money';
import { getReceiptAmountLocalTotal } from '@tectonics/prs/receipts/receipt';

import waitingIcon from './waiting-icon.svg';

export function CreditCardPageContent(): Node {
  const incomingPayment = usePendingIncomingPayment();
  const merchantName = getName(getMerchant(incomingPayment));
  const country = getCountry(incomingPayment);
  const receipt = useCurrentReceipt();
  const decimalFormat = getMoneyFormat(getCountry(incomingPayment));
  const formatAmount = amountToCurrencyString(decimalFormat);
  const tokenizedCard = getTokenizedCard(incomingPayment);
  const redirectUrl = getRedirectUrlAfterPayment(incomingPayment);

  if (!tokenizedCard) {
    return <Empty />;
  }

  const lastFourDigits = getLastFourDigits(tokenizedCard);

  return(
    <Root>

      <Image
        src={waitingIcon}
        alt="Waiting"
      />

      <CreditcardTitle>
        <CreditcardTitleMessage country={country} />
      </CreditcardTitle>

      <PaymentInfo>
        <CardMessage country={country} lastFourDigits={lastFourDigits} amount={formatAmount(getReceiptAmountLocalTotal(receipt))} merchantName={merchantName} />
      </PaymentInfo>

      <Separator direction="vertical" pattern="dotted" size={5} />

      <PaymentInfoRedirect>
        <PaymentInfoRedirectMessage country={country} />
      </PaymentInfoRedirect>

      <RedirectMerchantPage as="a" href={redirectUrl} target="blank" >
        <RedirectMerchantPageMessage country={country} merchantName={merchantName} />
      </RedirectMerchantPage>
    </Root>
  );
}

type RedirectMerchantPageMessageProps = {|
  country: Country,
  merchantName: string,
|}

function RedirectMerchantPageMessage({ country, merchantName }: RedirectMerchantPageMessageProps): Node {
  switch (country) {
    case 'br':
      return `Voltar para ${merchantName}`;
    case 'ar':
    case 'co':
    case 'mx':
    case 'pe':
    case 'py':
    case 'uy':
      return `Volver a ${merchantName}`;
    default:
      throw new Error(`PageContentCreditCard - invalid country ${country}`);
  }
}

type CardMessageProps = {|
  country: Country,
  lastFourDigits: string,
  amount: string,
  merchantName: string,
|}

function CardMessage({country, lastFourDigits, amount, merchantName}: CardMessageProps): Node {
  switch (country) {
    case 'br':
      return `O pagamento feito com o cartão •••• ${lastFourDigits}, no valor de ${amount} no site ${merchantName} foi recebido! Em breve você receberá um email com mais detalhes.`;
    case 'ar':
    case 'co':
    case 'mx':
    case 'pe':
    case 'py':
    case 'uy':
      return `¡Se recibió el pago realizado con la tarjeta •••• ${lastFourDigits}, por valor de ${amount} en el sitio web de ${merchantName}! Pronto recibirás un correo electrónico con más detalles.`;
    default:
      throw new Error(`PageContentCreditCard - invalid country ${country}`);
  }
}

type CreditcardTitleMessageProps = {
  country: Country,
}

function CreditcardTitleMessage({country}: CreditcardTitleMessageProps) : Node {
  switch (country) {
    case 'br':
      return 'Seu pagamento foi recebido :)';
    case 'ar':
    case 'co':
    case 'mx':
    case 'pe':
    case 'py':
    case 'uy':
      return 'Su pago ha sido recibido :)';
    default:
      throw new Error(`PageContentCreditCard - invalid country ${country}`);
  }
}

type PaymentInfoRedirectMessageProps = {
  country: Country,
}

function PaymentInfoRedirectMessage({country}: PaymentInfoRedirectMessageProps) : Node {
  switch (country) {
    case 'br':
      return 'Se quiser, clique no botão abaixo para voltar ao site em que a compra foi realizada.';
    case 'ar':
    case 'co':
    case 'mx':
    case 'pe':
    case 'py':
    case 'uy':
      return 'Si lo desea, haga clic en el botón de abajo para volver al sitio web donde se realizó la compra.';
    default:
      throw new Error(`PageContentCreditCard - invalid country ${country}`);
  }
}

const Root = styled.div`
  @media (min-width: 450px) {
    width: 477px;
  }
`;

const RedirectMerchantPage = styled(Button)`
  background: rgb(35, 151, 212);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  border-radius: 5px;
  width: 100%;

  @media (min-width: 450px) {
    width: 238px;
    margin: auto;
    display: block;
  }
`;

const CreditcardTitle = styled.p`
  text-align: center;
  font-size: 22px;
  font-family: ${props => props.theme.fontFamily.display};
  color: #2397d4;
  margin: 0 0 21px;
`;

const PaymentInfo = styled.p`
  text-align: center;
  margin-bottom: 16px;
  font-weight: 400;
  font-family: ${props => props.theme.fontFamily.display};
`;

const PaymentInfoRedirect = styled.p`
  text-align: center;
  margin: 21px 0 21px;
  font-weight: 400;
  font-family: ${props => props.theme.fontFamily.display};
`;

const Image = styled.img`
  flex: 0 0 auto;
  height: 33px;
  width: 33px;
  margin: 0 auto 21px;
  display: block;
`;
